import {
  correctionDate,
  riskPriorityLabel,
  type PreConsultSummary,
} from "@care-by-ella/contracts";
import { answerAnchorId } from "@/lib/flag-links";
import { flagRuleLabel } from "@/lib/flag-rule-labels";
import { escalationLabel, levelLabel } from "@/lib/state-labels";

// One rendering of a `PreConsultSummary`'s sections — the answers grouped
// the way the patient saw them, labels and values already resolved by the
// API against the version she submitted. Shared by the "Översikt" tab (F-02,
// CAR-118) and the "receptövertag" case view (F-07 + F-02, TES-458) so the two
// never drift in how a section reads.
//
// `after` renders below a section's rows — the case view uses it to list the
// attachments under the section they were uploaded in.
export function SummarySections({
  sections,
  after,
  anchorScope,
  highlightKey,
}: {
  sections: PreConsultSummary["sections"];
  after?: (sectionId: string) => React.ReactNode;
  // TES-1030 — when set, every answer row gets an anchor id a flag can
  // scroll to, and the row whose key is `highlightKey` is highlighted.
  anchorScope?: string;
  highlightKey?: string | null;
}) {
  return (
    <div className="flex flex-col gap-md">
      {sections.map((sec) => (
        <div key={sec.id}>
          <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide mb-xs">
            {sec.title}
          </h3>
          <div className="flex flex-col gap-xs">
            {sec.items.map((it) => (
              <div
                key={it.key}
                id={
                  anchorScope ? answerAnchorId(anchorScope, it.key) : undefined
                }
                className={`flex justify-between gap-md text-sm scroll-mt-lg rounded-sm transition-colors ${
                  anchorScope && highlightKey === it.key
                    ? "bg-warning/15 ring-2 ring-warning/60"
                    : ""
                }`}
              >
                <span className="text-textSecondary">{it.label}</span>
                <span className="text-right">
                  {it.value}
                  {/* TES-973 — a doctor's correction replaced her answer:
                      say so, and keep what she declared beside it. */}
                  {it.correction ? (
                    <span className="block text-xs text-textSecondary">
                      korrigerad av läkare{" "}
                      {correctionDate(it.correction.correctedAt)}
                      {it.correction.correctedBy
                        ? ` (${it.correction.correctedBy})`
                        : ""}{" "}
                      • patienten angav:{" "}
                      {it.correction.originalValue ?? "Ingen uppgift"}
                    </span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
          {after?.(sec.id)}
        </div>
      ))}
    </div>
  );
}

// Risk flags as the summary orders them: hard stops first, then flags by
// severity. Same colouring as the "Översikt" tab's two lists.
const RISK_COLOR: Record<string, string> = {
  low: "text-textSecondary border-surfaceMuted",
  medium: "text-warning border-warning/40",
  high: "text-warning border-warning/60",
  urgent: "text-danger border-danger/60",
};

export function RiskFlagList({
  flags,
  linkedKey,
  onOpen,
}: {
  flags: PreConsultSummary["riskFlags"];
  // TES-1030 — the answer a flag links to (null: no link), and what a click
  // does with it. Without them the list renders as before.
  linkedKey?: (flag: PreConsultSummary["riskFlags"][number]) => string | null;
  onOpen?: (fieldKey: string) => void;
}) {
  if (flags.length === 0) {
    return (
      <p className="text-sm text-textSecondary">
        Inga kontraindikationer eller flaggor.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-xs text-sm">
      {flags.map((f) => (
        <li
          key={`${f.kind}:${f.rule}`}
          className={`border-l-2 pl-sm ${
            f.kind === "hard_stop"
              ? "text-danger border-danger/60"
              : (RISK_COLOR[f.severity] ?? "")
          }`}
        >
          {(() => {
            const key = onOpen && linkedKey ? linkedKey(f) : null;
            return key ? (
              <button
                type="button"
                title={f.rule}
                onClick={() => onOpen!(key)}
                className="text-sm text-left underline decoration-dotted underline-offset-2 hover:text-brand"
              >
                {flagRuleLabel(f.rule)}
              </button>
            ) : (
              <span className="text-sm" title={f.rule}>
                {flagRuleLabel(f.rule)}
              </span>
            );
          })()}
          {f.riskCode ? (
            <span className="text-textSecondary">
              {" "}
              • Riskkod {f.riskCode} • Prioritet{" "}
              {riskPriorityLabel(f.riskPriority)}
            </span>
          ) : null}
          <span className="text-textSecondary">
            {" "}
            •{" "}
            {f.kind === "hard_stop"
              ? "kontraindikation"
              : levelLabel(f.severity)}
            {f.kind === "flag" && f.escalate && f.escalate !== "none"
              ? ` • eskalera ${escalationLabel(f.escalate)}`
              : ""}
          </span>
          {f.detail ? (
            <div className="text-textSecondary text-xs">{f.detail}</div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
